import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { X, Save, Trash2, Eye, Edit2, Sparkles, Package, Tag, ShoppingCart, Loader2, ImageIcon } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ProductDraft } from './ProductDialog';
import { VENDOR_PRESETS, getComboKey } from '../vendorPresets';
import { procurementSku } from '../procurementSku';
import { Button } from '../../components/ui/Button';
import { ImagePicker } from '../../components/ui/ImagePicker';
import { InclusionListEditor } from './InclusionListEditor';
import { InclusionAppsEditor } from './InclusionAppsEditor';
import { MicrosoftLicenceField, type LinkedProductRef } from './MicrosoftLicenceField';
import { resolvedMicrosoftSkuId } from '../microsoftLicenceLink';
import { marketplaceService } from '../../services/marketplaceService';
import { getEnrichment, type EnrichmentSourceProduct } from '../../services/distributorService';
import {
  attributesToFeatureRows,
  canonicalFeatureEditRows,
  editableRowsToFeatureAttributes,
  featureListToAttributes,
  type CanonicalFeatureKey,
} from '../productFeatureSpecs';

interface ProductDetailModalProps {
  open: boolean;
  product: ProductDraft | null;
  isLoading?: boolean;
  onClose: () => void;
  onSave: () => void;
  onUpdate: (next: ProductDraft) => void;
  /** Opens the parent-owned image picker for the product's primary image. */
  onOpenImagePicker?: () => void;
  /** True while the parent-owned image picker is stacked above this dialog. */
  imagePickerOpen?: boolean;
  /** Optional delete handler — shown only for non-distributor products. */
  onDelete?: () => void;
  getCachedImageUrl?: (url: string | undefined) => string | undefined;
  /** The partner's products, so the Microsoft licence field can flag a licence linked twice. */
  otherProducts?: readonly LinkedProductRef[];
}

const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect fill="%23f1f5f9" width="80" height="80" rx="8"/><text fill="%2394a3b8" font-family="Arial" font-size="12" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">No Image</text></svg>';

type Domain = ProductDraft['domain'];

const isMicrosoftPlan = (p: ProductDraft) => p.vendorPreset === 'microsoft';
const isDeleteLocked = (p: ProductDraft) =>
  p.source === 'dickerdata' || p.source === 'telstra-distributor';
const isDistributorSource = (source: ProductDraft['source']) =>
  source === 'dickerdata' || source === 'ingram' || source === 'telstra-distributor';
// The SKU on a distributor-sourced product is that distributor's part number,
// and the price/stock sync matches snapshot rows on it. Editing it detaches
// the product from its feed, so the field is read-only wherever it applies.
const isSyncedSku = (p: ProductDraft) => isDistributorSource(p.source);
const hasDistributorBuyPrice = (source: ProductDraft['source']) =>
  source === 'dickerdata' || source === 'ingram';
const getDistributorLabel = (source: ProductDraft['source']) => {
  if (source === 'dickerdata') return 'Dicker Data';
  if (source === 'ingram') return 'Ingram Micro';
  if (source === 'telstra-distributor') return 'Telstra';
  return 'Distributor';
};
const isImagePickerTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement && !!target.closest('[data-image-picker-root]');

type IngramSourceState = {
  loading: boolean;
  source: EnrichmentSourceProduct | null;
  rawResponse: unknown | null;
  error: string | null;
};

/**
 * True when a product is a distributor-sourced hardware item whose features
 * should be derived from the canonical per-SKU enrichment.attributes blob
 * (same wording, same order, every partner). Manual hardware keeps the
 * partner-editable features list since there's no canonical SKU to align to.
 */
const isDistributorHardware = (p: ProductDraft) =>
  (p.source === 'dickerdata' || p.source === 'ingram' || p.source === 'telstra-distributor') &&
  (p.domain === 'computers' || p.domain === 'unified-collaboration' || p.domain === 'mobile-hardware');

export function ProductDetailModal({
  open,
  product,
  isLoading,
  onClose,
  onSave,
  onUpdate,
  onOpenImagePicker,
  imagePickerOpen,
  onDelete,
  getCachedImageUrl,
  otherProducts,
}: ProductDetailModalProps) {
  // Local picker for *inclusion icons* (separate from the product's main image picker
  // owned by PartnerMarketplaceView). Holds an `apply` callback that targets the
  // specific row whose icon button was clicked.
  const [iconPicker, setIconPicker] = useState<{
    apply: (url: string) => void;
  } | null>(null);

  const onPickIcon = useCallback(
    (apply: (url: string) => void) => setIconPicker({ apply }),
    [],
  );

  // Customer-view toggle: shows the partner what their end customers see.
  // Defaults to Edit on open — partners are managing their catalogue here,
  // so the form they came to change is what should land first. They can
  // still toggle to Customer view to preview the storefront layout where
  // it's supported (Telstra plans and handsets have custom configurators).
  const supportsCustomerView = product?.domain !== 'telstra' && product?.domain !== 'mobile-hardware';
  const [viewMode, setViewMode] = useState<'customer' | 'edit'>('edit');
  const [ingramSource, setIngramSource] = useState<IngramSourceState>({
    loading: false,
    source: null,
    rawResponse: null,
    error: null,
  });

  useEffect(() => {
    if (!open || product?.source !== 'ingram' || !product.sku) {
      setIngramSource({ loading: false, source: null, rawResponse: null, error: null });
      return;
    }

    let cancelled = false;
    setIngramSource({ loading: true, source: null, rawResponse: null, error: null });
    getEnrichment('ingram', product.sku, { live: true })
      .then((detail) => {
        if (cancelled) return;
        setIngramSource({
          loading: false,
          source: detail.source ?? null,
          rawResponse: detail.source?.rawApiResponse ?? null,
          error: null,
        });
      })
      .catch((error: any) => {
        if (cancelled) return;
        setIngramSource({
          loading: false,
          source: null,
          rawResponse: null,
          error: error?.message || 'Unable to load Ingram raw response',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [open, product?.source, product?.sku]);

  if (!open || !product) return null;

  const domain: Domain = product.domain;
  const isMicrosoft = isMicrosoftPlan(product);
  const showInclusionsEditor = domain === 'telstra' || product.kind === 'software';
  const showFeaturesEditor = domain === 'computers' || domain === 'unified-collaboration';
  const imageUrl = getCachedImageUrl ? getCachedImageUrl(product.image) : product.image;

  // Helper: typed update
  const update = (patch: Partial<ProductDraft>) => onUpdate({ ...product, ...patch });
  const nestedImagePickerOpen = imagePickerOpen || !!iconPicker;

  return (
    // The image picker is portaled outside this dialog, so let that top-level
    // overlay own pointer/focus events while it is stacked above the product form.
    <DialogPrimitive.Root
      modal={!nestedImagePickerOpen}
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-50 w-[95vw] max-w-5xl max-h-[92vh] translate-x-[-50%] translate-y-[-50%] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden focus:outline-none"
          aria-describedby={undefined}
          onInteractOutside={(event) => {
            if (isImagePickerTarget(event.target)) {
              event.preventDefault();
            }
          }}
        >
          {/* Hidden title for a11y */}
          <DialogPrimitive.Title className="sr-only">
            {product.name || 'Edit product'}
          </DialogPrimitive.Title>

          {/* Header strip */}
          <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3 flex-shrink-0">
            <div className="h-12 w-12 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
              <img
                src={imageUrl || PLACEHOLDER_IMAGE}
                alt={product.name}
                className="max-h-10 max-w-10 object-contain"
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = PLACEHOLDER_IMAGE; }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-slate-900 truncate m-0">
                {product.name || 'New product'}
              </h2>
              <p className="text-xs text-slate-500 truncate m-0">
                {product.vendorOrBrand || (isMicrosoft ? 'Microsoft' : 'Vendor not set')}
                {product.sku && <span className="ml-2 font-mono">· {product.sku}</span>}
              </p>
            </div>
            {/* Edit / Customer view segmented toggle. Edit lands first
                (default) but Customer view is a single click away — kept at
                full text contrast (slate-700 idle) so it reads as
                accessible. Only goes muted-grey + disabled for Telstra
                product types, where the storefront preview shape isn't built yet. */}
            <div
              className="inline-flex items-center rounded-md border border-slate-200 bg-slate-50 p-0.5 mr-1"
              role="tablist"
              aria-label="Product view mode"
            >
              <button
                type="button"
                onClick={() => setViewMode('edit')}
                role="tab"
                aria-selected={viewMode === 'edit'}
                className={`inline-flex items-center gap-1.5 px-2.5 h-7 text-xs font-medium rounded transition-colors ${
                  viewMode === 'edit'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-700 hover:bg-white/70 hover:text-slate-900'
                }`}
              >
                <Edit2 className="h-3.5 w-3.5" />
                Edit
              </button>
              <button
                type="button"
                onClick={() => supportsCustomerView && setViewMode('customer')}
                disabled={!supportsCustomerView}
                role="tab"
                aria-selected={viewMode === 'customer'}
                title={supportsCustomerView
                  ? 'See how this product appears in the customer storefront'
                  : 'Customer-view preview not available yet for this domain'}
                className={`inline-flex items-center gap-1.5 px-2.5 h-7 text-xs font-medium rounded transition-colors ${
                  viewMode === 'customer' && supportsCustomerView
                    ? 'bg-white text-slate-900 shadow-sm'
                    : supportsCustomerView
                    ? 'text-slate-700 hover:bg-white/70 hover:text-slate-900'
                    : 'text-slate-300 cursor-not-allowed'
                }`}
              >
                <Eye className="h-3.5 w-3.5" />
                Customer view
              </button>
            </div>
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </DialogPrimitive.Close>
          </div>

          {isLoading ? (
            <div className="flex-1 flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
              <span className="ml-3 text-slate-600">Loading details...</span>
            </div>
          ) : viewMode === 'customer' && supportsCustomerView ? (
            <>
              <div className="flex-1 overflow-auto bg-slate-50">
                <CustomerProductView product={product} imageUrl={imageUrl} />
              </div>
              <div className="px-5 py-3 border-t border-slate-200 bg-white flex items-center justify-between flex-shrink-0">
                <div className="text-xs text-slate-500">
                  This is the layout your end customers see in the storefront. Click <span className="font-medium text-slate-700">Edit</span> to change anything.
                </div>
                <Button variant="outline" size="sm" onClick={onClose}>
                  Close
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Two-pane body */}
              <div className="flex-1 overflow-hidden flex flex-col md:flex-row min-h-0">
                {/* ── Left pane: identity + content ── */}
                <div className="flex-1 overflow-auto p-5 space-y-4 min-w-0">
                  <IdentitySection product={product} update={update} />

                  {isMicrosoft && (product.kind === 'software' || product.domain === 'software') && (
                    <MicrosoftLicenceField
                      key={product.id ?? 'new'}
                      productId={product.id}
                      productName={product.name}
                      vendor={product.vendorOrBrand}
                      value={resolvedMicrosoftSkuId(product)}
                      onChange={(microsoftSkuId) => update({ microsoftSkuId })}
                      otherProducts={otherProducts}
                    />
                  )}

                  {domain !== 'telstra' && (
                    <>
                      <DescriptionSection product={product} update={update} />
                      {product.source === 'ingram' && (
                        <IngramSourceDescriptionCard state={ingramSource} />
                      )}
                    </>
                  )}

                  {/* Microsoft software-only: app icon grid (M365 plans, not Surface hardware) */}
                  {isMicrosoft && product.kind === 'software' && (
                    <InclusionAppsEditor
                      value={product.inclusionApps}
                      onChange={(next) => update({ inclusionApps: next })}
                      onPickIcon={onPickIcon}
                      getCachedImageUrl={getCachedImageUrl}
                    />
                  )}

                  {/* Plan inclusions (telstra + all software) */}
                  {showInclusionsEditor && (
                    <InclusionListEditor
                      label={domain === 'telstra' ? 'Plan Inclusions' : 'Plan Includes'}
                      helperText={
                        domain === 'telstra'
                          ? "What's included in this plan (shown as a checklist on the customer view)"
                          : 'Key features customers see in the plan detail modal'
                      }
                      placeholder={
                        domain === 'telstra'
                          ? 'e.g., Unlimited calls & SMS to standard Australian numbers'
                          : 'e.g., 1 TB cloud storage per user'
                      }
                      value={product.planInclusions}
                      onChange={(next) => update({ planInclusions: next })}
                      onPickIcon={onPickIcon}
                      getCachedImageUrl={getCachedImageUrl}
                    />
                  )}

                  {/* Features (computers / unified-collaboration) — partner-editable only for non-distributor SKUs */}
                  {showFeaturesEditor && !isDistributorHardware(product) && (
                    <InclusionListEditor
                      label="Features"
                      helperText="Hardware specs / key features (shown as bullet list)"
                      placeholder="e.g., 16GB RAM, 512GB SSD"
                      value={product.features}
                      onChange={(next) => update({ features: next })}
                      onPickIcon={onPickIcon}
                      getCachedImageUrl={getCachedImageUrl}
                    />
                  )}
                  {/* Canonical features panel — distributor-sourced hardware (computers, UC, mobile) */}
                  {isDistributorHardware(product) && (
                    <CanonicalFeaturesPanel
                      attributes={{
                        ...featureListToAttributes(product.features),
                        ...(product.enrichmentAttributes ?? {}),
                      }}
                      onChange={(next) => {
                        update({
                          enrichmentAttributes: next,
                          features: attributesToFeatureRows(next).map((row) => row.text),
                        });
                      }}
                    />
                  )}
                  {product.source === 'ingram' && (
                    <IngramRawResponseCard state={ingramSource} />
                  )}
                </div>

                {/* ── Right pane: pricing + meta (sticky) ── */}
                <div className="w-full md:w-96 flex-shrink-0 border-t md:border-t-0 md:border-l border-slate-200 bg-slate-50 flex flex-col">
                  <div className="flex-1 overflow-auto p-5 space-y-4">
                    <ProductImagePane
                      product={product}
                      imageUrl={imageUrl}
                      update={update}
                      onOpenImagePicker={onOpenImagePicker}
                    />
                    <PricingPane product={product} update={update} />
                    <MetaPane product={product} update={update} />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-3 border-t border-slate-200 bg-white flex items-center justify-between flex-shrink-0">
                <div>
                  {onDelete && !isDeleteLocked(product) && (
                    <button
                      type="button"
                      onClick={onDelete}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={onSave}
                    disabled={!product.domain}
                  >
                    <Save className="w-4 h-4 mr-1.5" />
                    Save changes
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>

      {/* Local icon picker — separate from the product's main image picker */}
      {iconPicker && (
        <ImagePicker
          isOpen={true}
          onClose={() => setIconPicker(null)}
          onSelectImage={(url) => {
            iconPicker.apply(url);
            setIconPicker(null);
          }}
          category="software"
          title="Choose icon"
          allowUpload={true}
        />
      )}
    </DialogPrimitive.Root>
  );
}

// ────────────────────────────────────────────────────────────────────────────────
// Sub-sections — kept inline in this file to avoid prop-drilling explosion.
// ────────────────────────────────────────────────────────────────────────────────

function IdentitySection({
  product,
  update,
}: {
  product: ProductDraft;
  update: (patch: Partial<ProductDraft>) => void;
}) {
  const isSoftware = product.kind === 'software';
  const subCategoryDomains: ProductDraft['domain'][] = ['computers', 'networking', 'unified-collaboration', 'software'];
  const showSubCategory = subCategoryDomains.includes(product.domain);

  return (
    <section className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Product Name</label>
        <input
          type="text"
          value={product.name}
          onChange={(e) => update({ name: e.target.value })}
          className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Vendor / Brand</label>
          {isSoftware ? (
            <div className="space-y-1.5">
              <select
                value={product.vendorPreset || '_custom'}
                onChange={(e) => {
                  const presetId = e.target.value;
                  if (presetId === '_custom') {
                    update({ vendorPreset: undefined, vendorOrBrand: '', termPricing: undefined, microsoftSkuId: undefined });
                  } else {
                    const preset = VENDOR_PRESETS.find((p) => p.id === presetId);
                    if (preset) {
                      const basePrice = product.price || 0;
                      const tp: Record<string, number> = {};
                      for (const combo of preset.validCombinations) {
                        tp[getComboKey(combo.billingCycle, combo.term)] =
                          product.termPricing?.[getComboKey(combo.billingCycle, combo.term)] ?? basePrice;
                      }
                      update({
                        vendorPreset: preset.id,
                        vendorOrBrand: preset.label,
                        termPricing: tp,
                        term: preset.defaultTerm,
                        billingCycle: preset.defaultBillingCycle,
                      });
                    }
                  }
                }}
                className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {VENDOR_PRESETS.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
                <option value="_custom">Other / Custom...</option>
              </select>
              {!product.vendorPreset && (
                <input
                  type="text"
                  value={product.vendorOrBrand || ''}
                  onChange={(e) => update({ vendorOrBrand: e.target.value })}
                  placeholder="Enter vendor name"
                  className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              )}
            </div>
          ) : (
            <input
              type="text"
              value={product.vendorOrBrand || ''}
              onChange={(e) => update({ vendorOrBrand: e.target.value })}
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">
            Domain <span className="text-rose-500">*</span>
          </label>
          <select
            value={product.domain || ''}
            onChange={(e) => {
              const d = e.target.value as ProductDraft['domain'];
              const kind: ProductDraft['kind'] =
                d === 'software' ? 'software' : d === 'consulting' ? 'consulting' : 'hardware';
              update({ domain: d, kind });
            }}
            className={`w-full rounded-md border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 ${
              !product.domain ? 'border-rose-300' : 'border-slate-300'
            }`}
          >
            <option value="" disabled>Select domain</option>
            <option value="software">Software</option>
            <option value="computers">Computers</option>
            <option value="networking">Networking</option>
            <option value="mobile-hardware">Telstra Handsets</option>
            <option value="consulting">Consulting</option>
            <option value="telstra">Telstra</option>
            <option value="unified-collaboration">Unified Collaboration</option>
          </select>
          {!product.domain && (
            <p className="text-[11px] text-rose-500 mt-0.5">Required for procurement routing</p>
          )}
        </div>
      </div>

      {showSubCategory && (
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Sub-category</label>
          <select
            value={product.subCategory || ''}
            onChange={(e) => update({ subCategory: e.target.value || undefined })}
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="">Not specified</option>
            {product.domain === 'computers' && (
              <>
                <option value="Laptop">Laptop</option>
                <option value="Desktop">Desktop</option>
              </>
            )}
            {product.domain === 'networking' && (
              <>
                <option value="Router">Router</option>
                <option value="Firewall">Firewall</option>
                <option value="Switch">Switch</option>
                <option value="Access Point">Access Point</option>
              </>
            )}
            {product.domain === 'unified-collaboration' && (
              <>
                <option value="Headsets">Headsets</option>
                <option value="Conferencing">Conferencing</option>
              </>
            )}
            {product.domain === 'software' && (
              <>
                <option value="Productivity">Productivity</option>
                <option value="Security">Security</option>
              </>
            )}
          </select>
        </div>
      )}

      {product.domain === 'telstra' && (
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">
            Telstra Product Type <span className="text-rose-500">*</span>
          </label>
          <select
            value={product.telstraProductType || ''}
            onChange={(e) => update({ telstraProductType: e.target.value as 'mobile_voice' | 'mobile_broadband' })}
            className={`w-full rounded-md border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 ${
              !product.telstraProductType ? 'border-rose-300' : 'border-slate-300'
            }`}
          >
            <option value="" disabled>Select type</option>
            <option value="mobile_voice">Mobile Voice</option>
            <option value="mobile_broadband">Mobile Broadband</option>
          </select>
        </div>
      )}

      {product.domain === 'mobile-hardware' && (
        <MobileHardwareFields product={product} update={update} />
      )}

      {product.domain !== 'telstra' && (
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">
            SKU
            {isSyncedSku(product) && <span className="ml-1 text-xs text-sky-600">(synced)</span>}
          </label>
          <input
            type="text"
            value={product.sku || ''}
            onChange={(e) => update({ sku: e.target.value || undefined })}
            disabled={isSyncedSku(product)}
            placeholder={isSyncedSku(product) ? '' : 'Enter SKU'}
            className={`w-full rounded-md border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono ${
              isSyncedSku(product) ? 'border-slate-200 bg-slate-100 text-slate-600 cursor-not-allowed' : 'border-slate-300'
            }`}
          />
          {isSyncedSku(product) && (
            <p className="mt-1 text-xs text-slate-500">
              {getDistributorLabel(product.source)} part number — used to match price and stock updates.
              Customers see the vendor part number instead.
            </p>
          )}
          {!isSyncedSku(product) && (product.kind === 'software' || product.domain === 'software') && product.vendorPreset === 'microsoft' && (
            <p className="mt-1 text-xs text-slate-500">
              Supplier part number. Optional. This is not the Microsoft licence.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function MobileHardwareFields({
  product,
  update,
}: {
  product: ProductDraft;
  update: (patch: Partial<ProductDraft>) => void;
}) {
  const colours = product.availableColours || [];
  return (
    <>
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Capacity</label>
        <input
          type="text"
          value={product.capacity || ''}
          onChange={(e) => update({ capacity: e.target.value || undefined })}
          placeholder="256GB"
          className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>
      <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
        <div className="flex items-center justify-between mb-2">
          <div>
            <label className="block text-xs font-medium text-slate-700">Available Colours</label>
            <p className="text-[11px] text-slate-500">Up to 5 options for customer selection</p>
          </div>
          <button
            type="button"
            onClick={() => {
              if (colours.length >= 5) return;
              update({ availableColours: [...colours, ''] });
            }}
            disabled={colours.length >= 5}
            className={`px-2 py-1 rounded text-xs font-medium ${
              colours.length >= 5
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                : 'bg-orange-600 text-white hover:bg-orange-700'
            }`}
          >
            + Add
          </button>
        </div>
        {colours.length === 0 ? (
          <div className="text-[11px] text-slate-400 text-center py-2 border border-dashed border-slate-300 rounded bg-white">
            No colours defined.
          </div>
        ) : (
          <div className="space-y-1.5">
            {colours.map((c, i) => (
              <div key={i} className="flex items-center gap-2 p-1.5 bg-white rounded border border-slate-200">
                <input
                  type="text"
                  value={c}
                  onChange={(e) => {
                    const next = [...colours];
                    next[i] = e.target.value;
                    update({ availableColours: next });
                  }}
                  placeholder="e.g., Black"
                  className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                />
                <button
                  type="button"
                  onClick={() => update({ availableColours: colours.filter((_, j) => j !== i) })}
                  className="p-1 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded"
                  aria-label="Remove colour"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">
          Device Repayment Eligibility
        </label>
        <select
          value={product.deviceEligibility || 'outright_only'}
          onChange={(e) => update({ deviceEligibility: e.target.value as 'outright_only' | 'mobile_voice' | 'mobile_broadband' })}
          className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="outright_only">Outright Only (no plan attachment)</option>
          <option value="mobile_voice">Eligible for Mobile Voice plans</option>
          <option value="mobile_broadband">Eligible for Mobile Broadband plans</option>
        </select>
      </div>
    </>
  );
}

function DescriptionSection({
  product,
  update,
}: {
  product: ProductDraft;
  update: (patch: Partial<ProductDraft>) => void;
}) {
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const onGenerate = useCallback(async () => {
    setGenerating(true);
    setGenError(null);
    try {
      const distributorId =
        product.source === 'dickerdata' || product.source === 'ingram'
          ? product.source
          : undefined;
      const featureStrings = (product.features ?? []).map((it: any) =>
        typeof it === 'string' ? it : (it?.text ?? ''),
      ).filter(Boolean);
      const result = await marketplaceService.generateProductDescription({
        productId: product.id,
        distributorId,
        sku: product.sku,
        name: product.name,
        vendor: product.vendorOrBrand,
        domain: product.domain,
        features: featureStrings,
        currentDescription: product.description,
      });
      if (result?.description) {
        update({ description: result.description });
      } else {
        setGenError('No description was generated. Try again or write one manually.');
      }
    } catch (e: any) {
      setGenError(e?.message || 'Failed to generate description.');
    } finally {
      setGenerating(false);
    }
  }, [product, update]);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-xs font-medium text-slate-700">Description</label>
        <button
          type="button"
          onClick={onGenerate}
          disabled={generating || !product.name}
          title={!product.name ? 'Set a product name first' : 'Generate a description with AI'}
          className="inline-flex items-center gap-1 text-[11px] text-orange-700 bg-orange-50 ring-1 ring-inset ring-orange-200 rounded-full px-2 py-0.5 hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {generating ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Sparkles className="h-3 w-3" />
          )}
          {generating ? 'Generating…' : 'Generate with AI'}
        </button>
      </div>
      <textarea
        value={product.description || ''}
        onChange={(e) => update({ description: e.target.value })}
        rows={4}
        placeholder="Enter product description (shown on customer detail view)..."
        className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
      />
      {genError && (
        <p className="text-xs text-rose-600 mt-1">{genError}</p>
      )}
    </div>
  );
}

function ProductImagePane({
  product,
  imageUrl,
  update,
  onOpenImagePicker,
}: {
  product: ProductDraft;
  imageUrl: string | undefined;
  update: (patch: Partial<ProductDraft>) => void;
  onOpenImagePicker?: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 uppercase tracking-wide m-0">
          <ImageIcon className="h-3.5 w-3.5 text-slate-400" />
          Product image
        </h4>
        {onOpenImagePicker && (
          <button
            type="button"
            onClick={onOpenImagePicker}
            className="inline-flex items-center justify-center h-7 px-2.5 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:border-orange-300 hover:text-orange-700 hover:bg-orange-50 transition-colors"
          >
            Select
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => onOpenImagePicker?.()}
        disabled={!onOpenImagePicker}
        className="w-full rounded-lg border border-slate-200 bg-white p-3 hover:border-orange-300 hover:bg-orange-50/40 transition-colors disabled:hover:border-slate-200 disabled:hover:bg-white disabled:cursor-default"
        title={onOpenImagePicker ? 'Choose product image' : undefined}
      >
        <div className="h-28 rounded-md bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden">
          <img
            src={imageUrl || PLACEHOLDER_IMAGE}
            alt={product.name || 'Product image'}
            className="max-h-24 max-w-full object-contain"
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = PLACEHOLDER_IMAGE; }}
          />
        </div>
      </button>

      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Image URL</label>
        <input
          type="text"
          value={product.image || ''}
          onChange={(e) => update({ image: e.target.value })}
          placeholder="https://... or pack:category/file.png"
          className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>
    </div>
  );
}

function PricingPane({
  product,
  update,
}: {
  product: ProductDraft;
  update: (patch: Partial<ProductDraft>) => void;
}) {
  const fixedPricingDomains: ProductDraft['domain'][] = [
    'mobile-hardware',
    'computers',
    'unified-collaboration',
  ];
  const lumpSumLocked = fixedPricingDomains.includes(product.domain);
  const telstraLocked = product.domain === 'telstra';
  const pricingType =
    lumpSumLocked ? 'lumpSum'
      : telstraLocked ? 'perUser'
      : (product.pricingType || (product.kind === 'software' ? 'perUser' : 'lumpSum'));
  const buyPrice = typeof product.buyPrice === 'number' ? product.buyPrice : null;
  const hasBuyPrice = hasDistributorBuyPrice(product.source) && buyPrice !== null;
  const buyPriceSourceLabel = getDistributorLabel(product.source);
  const lockSellPriceToMarkup = product.domain === 'software' && hasBuyPrice;

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide m-0">Pricing</h4>

      {/* Distributor source banners */}
      {product.source === 'telstra-distributor' && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5">
          <div className="text-[11px] text-amber-700 mb-0.5">Sell Price (locked to Telstra RRP)</div>
          <div className="text-base font-semibold text-amber-900">
            {typeof product.price === 'number' && product.price > 0 ? `$${product.price.toFixed(2)}` : 'Not set'}
          </div>
        </div>
      )}
      {hasDistributorBuyPrice(product.source) && (
        <div className="bg-sky-50 border border-sky-200 rounded-md p-2.5">
          <div className="text-[11px] text-sky-700 mb-0.5">Buy Price ({buyPriceSourceLabel})</div>
          <div className="text-base font-semibold text-sky-900">
            {typeof product.buyPrice === 'number' ? `$${product.buyPrice.toFixed(2)}` : 'Not set'}
          </div>
          {typeof product.rrpExTax === 'number' && product.rrpExTax > 0 && (
            <div className="text-[11px] text-sky-600 mt-1">RRP: ${product.rrpExTax.toFixed(2)}</div>
          )}
        </div>
      )}

      {/* Markup option for distributor buy-price products */}
      {hasBuyPrice && (
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">
            Markup {product.domain === 'software' && <span className="text-orange-600">(required)</span>}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              step="1"
              value={product.markupPercent ?? ''}
              onChange={(e) => {
                const m = parseFloat(e.target.value);
                if (!isNaN(m) && m >= 0 && buyPrice !== null) {
                  const newPrice = Math.round(buyPrice * (1 + m / 100) * 100) / 100;
                  update({ markupPercent: m, price: newPrice });
                } else {
                  update({ markupPercent: undefined });
                }
              }}
              placeholder="18"
              className="w-20 rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <span className="text-xs text-slate-600">% markup</span>
            {typeof product.markupPercent === 'number' && (
              <span className="text-xs text-emerald-600 font-medium">
                = ${(Math.round((buyPrice ?? 0) * (1 + product.markupPercent / 100) * 100) / 100).toFixed(2)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Vendor-preset term grid — only meaningful for software plans.
          Hardware products are lump-sum even when the vendor (e.g. Microsoft)
          has a preset attached from legacy data. */}
      {product.vendorPreset && product.kind === 'software' ? (
        (() => {
          const preset = VENDOR_PRESETS.find((p) => p.id === product.vendorPreset);
          if (!preset) return null;
          return (
            <div className="space-y-2">
              <div className="text-[11px] text-slate-500">Pricing per term/billing combination</div>
              <div className="grid grid-cols-2 gap-2">
                {preset.validCombinations.map((combo) => {
                  const key = getComboKey(combo.billingCycle, combo.term);
                  return (
                    <div key={key}>
                      <label className="block text-[11px] text-slate-600 mb-0.5">{combo.label}</label>
                      <div className="flex items-center gap-1">
                        <span className="text-slate-400 text-xs">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={product.termPricing?.[key] ?? 0}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            const next = { ...(product.termPricing || {}), [key]: val };
                            const vals = Object.values(next).filter((v): v is number => typeof v === 'number' && v > 0);
                            const derivedPrice = vals.length > 0 ? Math.min(...vals) : 0;
                            update({ termPricing: next, price: derivedPrice });
                          }}
                          className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()
      ) : (
        <>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              {product.source === 'telstra-distributor' ? 'Sell Price (locked)' : 'Sell Price'}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={product.price || 0}
              onChange={(e) => {
                const val = parseFloat(e.target.value) || 0;
                if (!lockSellPriceToMarkup) {
                  update({ price: val, markupPercent: undefined });
                }
              }}
              disabled={
                product.source === 'telstra-distributor' ||
                lockSellPriceToMarkup
              }
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Pricing Type</label>
            <select
              value={pricingType}
              onChange={(e) => update({ pricingType: e.target.value as 'perUser' | 'lumpSum' })}
              disabled={lumpSumLocked || telstraLocked}
              className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-slate-100 disabled:cursor-not-allowed"
            >
              <option value="perUser">Per User</option>
              <option value="lumpSum">Lump Sum</option>
            </select>
          </div>

          {!lumpSumLocked && !telstraLocked && pricingType !== 'lumpSum' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Commitment</label>
                <select
                  value={product.term ?? 12}
                  onChange={(e) => update({ term: parseInt(e.target.value) })}
                  className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="0">Casual</option>
                  <option value="6">6 months</option>
                  <option value="12">12 months</option>
                  <option value="18">18 months</option>
                  <option value="24">24 months</option>
                  <option value="36">36 months</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Billing</label>
                <select
                  value={product.billingCycle || 'monthly'}
                  onChange={(e) => update({ billingCycle: e.target.value as 'monthly' | 'annual' })}
                  className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MetaPane({
  product,
  update,
}: {
  product: ProductDraft;
  update: (patch: Partial<ProductDraft>) => void;
}) {
  const isComputer = product.domain === 'computers';
  const showStockInput =
    !isDistributorSource(product.source) &&
    (product.domain === 'computers' ||
      product.domain === 'unified-collaboration' ||
      product.domain === 'networking' ||
      product.domain === 'mobile-hardware');
  const showStockReadonly =
    isDistributorSource(product.source) &&
    product.domain !== 'software';

  return (
    <div className="space-y-3 pt-3 border-t border-slate-200">
      <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide m-0">Meta</h4>

      {showStockReadonly && (
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">
            Stock on Hand <span className="ml-1 text-xs text-sky-600">(synced)</span>
          </label>
          <div
            className={`w-full rounded-md border px-2 py-1.5 text-sm font-medium ${
              (product.soh ?? 0) > 10
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : (product.soh ?? 0) > 0
                ? 'border-amber-200 bg-amber-50 text-amber-700'
                : 'border-slate-200 bg-slate-100 text-slate-500'
            }`}
          >
            {(product.soh ?? 0) > 10
              ? `In Stock (${product.soh})`
              : (product.soh ?? 0) > 0
              ? `Low Stock (${product.soh})`
              : 'Out of Stock'}
          </div>
        </div>
      )}

      {showStockInput && (
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Stock on Hand</label>
          <input
            type="number"
            min="0"
            step="1"
            value={product.soh ?? ''}
            onChange={(e) => {
              const v = e.target.value === '' ? undefined : parseInt(e.target.value);
              update({ soh: v });
            }}
            placeholder="0"
            className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      )}

      {/* Featured toggle (only for computers) */}
      <div
        className={`flex items-center justify-between p-2 border rounded-md ${
          isComputer ? 'bg-amber-50 border-amber-200' : 'bg-slate-100 border-slate-200 opacity-60'
        }`}
      >
        <div>
          <label className={`block text-xs font-medium ${isComputer ? 'text-slate-700' : 'text-slate-500'}`}>
            Featured
          </label>
          <p className="text-[11px] text-slate-500">
            {isComputer ? "Show on customer's Featured page" : 'Only available for Computers'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => isComputer && update({ featured: !product.featured })}
          disabled={!isComputer}
          aria-pressed={!!product.featured}
          className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 ${
            !isComputer
              ? 'bg-slate-200 cursor-not-allowed'
              : product.featured
              ? 'bg-amber-500 cursor-pointer'
              : 'bg-slate-300 cursor-pointer'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
              product.featured ? 'translate-x-[18px]' : 'translate-x-0.5'
            } mt-0.5`}
          />
        </button>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Provisioning Notes</label>
        <textarea
          value={product.provisioningNotes || ''}
          onChange={(e) => update({ provisioningNotes: e.target.value || undefined })}
          rows={2}
          placeholder="Internal notes for fulfilment (not shown to customers)"
          className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────────
// CustomerProductView — what end customers see in the storefront. Read-only,
// composed entirely from the product's saved data. Domain-aware: hides
// fields that don't apply to the current domain (e.g. plan inclusions on
// hardware, features on software).
//
// Scope of this surface: all non-Telstra domains. Telstra plans and handsets
// use custom storefront configurators, so the toggle above keeps Customer view
// disabled for those.
// ────────────────────────────────────────────────────────────────────────────────

/**
 * Customer-facing storefront preview of a product. Exported so it can be
 * reused outside the edit modal — e.g. the catalogue reorder view's
 * read-only preview popup. Pure render: no edit affordances inside.
 */
export function CustomerProductView({
  product,
  imageUrl,
}: {
  product: ProductDraft;
  imageUrl?: string;
}) {
  const domain = product.domain;
  const isSoftware = domain === 'software' || product.kind === 'software';

  // Normalise the inclusions / features arrays — both can be either string[]
  // (legacy) or InclusionItem[] (rich). Strip blanks and pull through to a
  // displayable shape with optional icon.
  // For distributor-sourced hardware, prefer canonical enrichment.attributes
  // so all partners selling the same SKU show identical features.
  const featureRows = useMemo(() => {
    if (isDistributorHardware(product) && product.enrichmentAttributes) {
      return attributesToFeatureRows(product.enrichmentAttributes);
    }
    return normaliseInclusionList(product.features);
  }, [product]);
  const inclusionRows = useMemo(() => {
    return normaliseInclusionList(product.planInclusions);
  }, [product.planInclusions]);

  // Section header is computed inline below (showFeaturesAsInclusions branch).
  const showFeaturesAsInclusions = isSoftware && inclusionRows.length === 0 && featureRows.length > 0;
  const inclusionsToShow = isSoftware
    ? (inclusionRows.length > 0 ? inclusionRows : featureRows)
    : featureRows;

  const priceLine = formatPriceLine(product);
  const stockBadge = (typeof product.soh === 'number') ? stockTone(product.soh) : null;
  const displaySku = procurementSku(product);

  return (
    <div className="px-6 py-8 max-w-3xl mx-auto">
      {/* Hero card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-0">
          {/* Image */}
          <div className="bg-white border-r border-slate-100 flex items-center justify-center p-6 min-h-[240px]">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={product.name}
                className="max-h-56 max-w-full object-contain"
              />
            ) : (
              <div className="h-32 w-32 rounded-lg bg-slate-100 flex items-center justify-center">
                <Package className="h-12 w-12 text-slate-300" />
              </div>
            )}
          </div>
          {/* Body */}
          <div className="p-6 space-y-4">
            {(product.vendorOrBrand || domain) && (
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-2">
                {product.vendorOrBrand && <span>{product.vendorOrBrand}</span>}
                {product.vendorOrBrand && domain && <span className="text-slate-300">·</span>}
                {domain && <DomainPill domain={domain} />}
              </div>
            )}
            <h1 className="text-2xl font-semibold text-slate-900 leading-tight m-0">
              {product.name || 'Untitled product'}
            </h1>
            {displaySku && (
              <div className="text-xs text-slate-400 font-mono inline-flex items-center gap-1.5">
                <Tag className="h-3 w-3" /> {displaySku}
              </div>
            )}
            {product.description ? (
              <p className="text-sm text-slate-700 leading-relaxed m-0">
                {product.description}
              </p>
            ) : (
              <p className="text-sm text-slate-400 italic m-0">
                No description set yet — switch to Edit to add one.
              </p>
            )}

            {/* Price + stock + CTA */}
            <div className="border-t border-slate-100 pt-4 flex items-end justify-between gap-3">
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">Price</div>
                <div className="text-2xl font-semibold text-slate-900 tabular-nums mt-0.5">
                  {priceLine || '—'}
                </div>
                {stockBadge && (
                  <div className="text-[11px] mt-1 inline-flex items-center gap-1.5">
                    <span className={`inline-block h-1.5 w-1.5 rounded-full ${stockBadge.dot}`} />
                    <span className={stockBadge.text}>{stockBadge.label}</span>
                  </div>
                )}
              </div>
              <button
                type="button"
                disabled
                title="Storefront cosmetic — partners don't add to cart from here"
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 disabled:opacity-90 disabled:cursor-not-allowed"
              >
                <ShoppingCart className="h-4 w-4" />
                Add to cart
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Inclusions / Features */}
      {inclusionsToShow.length > 0 && (
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm mt-4 p-6">
          <h3 className="text-base font-semibold text-slate-900 mb-3 inline-flex items-center gap-1.5">
            {showFeaturesAsInclusions
              ? 'What you get'
              : isSoftware ? 'Plan includes' : 'Features'}
          </h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {inclusionsToShow.map((row, i) => (
              <li key={i} className="text-sm text-slate-700 inline-flex items-start gap-2">
                {row.icon ? (
                  <img src={row.icon} alt="" className="h-4 w-4 mt-0.5 rounded-sm flex-shrink-0" />
                ) : (
                  <span className="inline-block h-4 w-4 mt-0.5 text-emerald-500 flex-shrink-0">
                    <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                  </span>
                )}
                <span>{row.text}</span>
              </li>
            ))}
          </ul>
          {/* Inline subtitle just for software-with-features fallback */}
          {showFeaturesAsInclusions && (
            <p className="text-[11px] text-slate-400 mt-3">
              Built from the product's <code className="bg-slate-100 px-1 rounded">features</code> list — set <code className="bg-slate-100 px-1 rounded">planInclusions</code> in Edit for plan-style copy.
            </p>
          )}
        </section>
      )}

    </div>
  );
}

function DomainPill({ domain }: { domain: ProductDraft['domain'] }) {
  const map: Record<string, { label: string; cls: string }> = {
    computers:               { label: 'Computers',              cls: 'bg-slate-100 text-slate-700 ring-slate-200' },
    networking:              { label: 'Networking',             cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
    'unified-collaboration': { label: 'Unified Collaboration', cls: 'bg-cyan-50 text-cyan-700 ring-cyan-200' },
    software:                { label: 'Software',               cls: 'bg-violet-50 text-violet-700 ring-violet-200' },
    consulting:              { label: 'Consulting',             cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
  };
  const cfg = map[domain ?? ''] ?? { label: domain ?? '—', cls: 'bg-slate-100 text-slate-700 ring-slate-200' };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function CanonicalFeaturesPanel({
  attributes,
  onChange,
}: {
  attributes?: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const [rows, setRows] = useState(() => canonicalFeatureEditRows(attributes));
  const lastAppliedAttributesRef = useRef(attributeSignature(attributes));

  useEffect(() => {
    const signature = attributeSignature(attributes);
    if (signature !== lastAppliedAttributesRef.current) {
      setRows(canonicalFeatureEditRows(attributes));
      lastAppliedAttributesRef.current = signature;
    }
  }, [attributes]);

  const commitRows = (nextRows: Array<{ key: CanonicalFeatureKey; label: string; value: string }>) => {
    setRows(nextRows);
    const next = editableRowsToFeatureAttributes(nextRows);
    lastAppliedAttributesRef.current = attributeSignature(next);
    onChange(next);
  };
  const updateRow = (index: number, value: string) => {
    commitRows(rows.map((row, i) => (i === index ? { ...row, value } : row)));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-xs font-medium text-slate-700">
          Features <span className="ml-1 text-[10px] text-slate-400 font-normal">(synced specs)</span>
        </label>
      </div>
      <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 space-y-2">
        {rows.map((row, i) => (
          <div key={row.key} className="grid grid-cols-[132px_minmax(0,1fr)] gap-2 items-center">
            <div className="text-xs font-medium text-slate-600">{row.label}</div>
            <input
              type="text"
              value={row.value}
              onChange={(e) => updateRow(i, e.target.value)}
              className="min-w-0 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
              aria-label={row.label}
              placeholder={`Enter ${row.label.toLowerCase()}`}
            />
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-400 mt-1.5">
        Saved specs use the standard customer-facing order: Processor, Screen Size, RAM, Hard Drive, Cellular, Warranty.
      </p>
    </div>
  );
}

function attributeSignature(attributes: Record<string, unknown> | undefined): string {
  return JSON.stringify(attributes ?? {});
}

function IngramSourceDescriptionCard({ state }: { state: IngramSourceState }) {
  const source = state.source;
  const description = source?.displayDescription || source?.shortDescription || null;
  const category = source?.category
    ? [source.category.l1, source.category.l2, source.category.l3].filter(Boolean).join(' / ')
    : null;

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">From Ingram</div>
          <div className="text-[11px] text-slate-400">Raw distributor feed, not shown to customers</div>
        </div>
        {source?.vendorPartNumber && (
          <span className="rounded bg-white px-2 py-0.5 text-[11px] font-mono text-slate-500 ring-1 ring-slate-200">
            {source.vendorPartNumber}
          </span>
        )}
      </div>

      {state.loading ? (
        <p className="mt-3 text-xs text-slate-500">Loading Ingram source description...</p>
      ) : state.error ? (
        <p className="mt-3 text-xs text-rose-600">{state.error}</p>
      ) : (
        <div className="mt-3 space-y-3">
          <SourceField label="Description" value={description} />
          <SourceField label="Category" value={category} placeholder="-" />
        </div>
      )}
    </div>
  );
}

function SourceField({
  label,
  value,
  placeholder = '(none provided)',
}: {
  label: string;
  value: string | null | undefined;
  placeholder?: string;
}) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm ${value ? 'text-slate-800' : 'italic text-slate-400'}`}>
        {value || placeholder}
      </div>
    </div>
  );
}

function IngramRawResponseCard({ state }: { state: IngramSourceState }) {
  const json = state.rawResponse ? stringifyJson(state.rawResponse) : null;
  return (
    <details className="rounded-md border border-slate-200 bg-white">
      <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold text-slate-700">
        Ingram Micro raw API response
      </summary>
      <div className="border-t border-slate-100 px-3 py-2">
        {state.loading ? (
          <p className="text-xs text-slate-500">Loading raw Ingram response...</p>
        ) : state.error ? (
          <p className="text-xs text-rose-600">{state.error}</p>
        ) : json ? (
          <pre className="max-h-64 overflow-auto rounded-md bg-slate-950 p-3 text-[11px] leading-relaxed text-slate-100 whitespace-pre-wrap">
            {json}
          </pre>
        ) : (
          <p className="text-xs text-slate-500">No raw Ingram response available for this SKU.</p>
        )}
      </div>
    </details>
  );
}

function stringifyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function normaliseInclusionList(raw: ProductDraft['features'] | ProductDraft['planInclusions']): Array<{ text: string; icon?: string }> {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return (raw as Array<any>)
      .map((it) => {
        if (typeof it === 'string') return { text: it.trim() };
        if (it && typeof it === 'object') return { text: String(it.text ?? '').trim(), icon: it.icon ?? undefined };
        return { text: '' };
      })
      .filter((r) => r.text.length > 0);
  }
  return [];
}

function formatPriceLine(product: ProductDraft): string | null {
  const p = (product as any).price ?? (product as any).sellPrice;
  if (typeof p !== 'number' || p <= 0) return null;
  const money = `$${p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (product.pricingType === 'perUser') {
    return `${money} / user / ${product.billingCycle === 'annual' ? 'year' : 'month'}`;
  }
  return money;
}

function stockTone(soh: number): { label: string; text: string; dot: string } {
  if (soh > 10) return { label: `In stock (${soh})`, text: 'text-emerald-700', dot: 'bg-emerald-500' };
  if (soh > 0) return { label: `Low stock (${soh})`, text: 'text-amber-700', dot: 'bg-amber-500' };
  return { label: 'Out of stock', text: 'text-rose-700', dot: 'bg-rose-500' };
}
